<template>
  <div class="select-input">
    <dropdown
      flex
      :align="align"
      :active="active"
      @close="active = false"
    >
      <div
        :class="['input', { disabled, active, searchable }]"
        tabindex="0"
        @focus="onTabFocus"
      >
        <label :for="id">{{ label }}</label>

        <div class="value">{{ selectedLabel }}</div>

        <input
          ref="input"
          v-model="searchString"
          :disabled="disableSearch"
          @blur.stop.prevent="onInputBlur"
          @keydown="navigateItems"
        />

        <icon
          class="arrow"
          :name="active ? 'expand_less' : 'expand_more'"
        />
      </div>

      <template #menu>
        <dropdown-menu
          :options="filteredOptions"
          :activeValue="selectedValue"
          :focus="itemFocus"
          @select="handleSelect"
          @mouseenter="mouseover = 'menu'"
          @mouseleave="mouseover = ''"
        />
      </template>
    </dropdown>
  </div>
</template>

<script>
import Dropdown from './Dropdown';
import DropdownMenu from './DropdownMenu';
import Icon from './Icon';

export default {
  components: {
    Dropdown,
    DropdownMenu,
    Icon,
  },

  props: {
    value: [String, Boolean, Number],
    id: String,
    label: String,
    options: Array,
    searchable: Boolean,
    disabled: Boolean,
    align: {
      type: String,
      default: 'left',
    },
  },

  data() {
    return {
      selected: null,
      active: false,
      searchString: '',
      itemFocus: null,
      mouseover: false,
    };
  },

  created() {
    this.setSelected();
  },

  watch: {
    active(value) {
      if (value) {
        this.$refs.input.focus();
      } else {
        this.searchString = '';
        this.$refs.input.blur();
      }
    },

    value() {
      this.setSelected();
    },
  },

  computed: {
    selectedLabel() {
      return this.selected ? this.selected.label : '';
    },

    selectedValue() {
      return this.selected ? this.selected.value : null;
    },

    disableSearch() {
      return !this.searchable || this.disabled;
    },

    filteredOptions() {
      if (!this.searchable || !this.searchString) {
        return this.options;
      }

      return this.options.filter(({ label }) => label.toLowerCase().indexOf(this.searchString.toLowerCase()) > -1);
    },
  },

  methods: {
    setSelected() {
      if (this.value === null) {
        this.selected = null;
      } else {
        this.selected = this.options.find(({ value }) => value === this.value);
      }
    },

    handleSelect(value) {
      this.$emit('input', value);
      this.active = false;
    },

    navigateItems(e) {
      try {
        switch (e.keyCode) {
          case 13:
            this.handleSelect(this.filteredOptions[this.itemFocus || 0].value);
            this.itemFocus = null;
            break;
          case 38:
            if (this.itemFocus === null) {
              this.itemFocus = 0;
            } else if (this.itemFocus > 0) {
              this.itemFocus--;
            }
            break;
          case 40:
            if (this.itemFocus === null) {
              this.itemFocus = 0;
            } else if (this.itemFocus < this.filteredOptions.length - 1) {
              this.itemFocus++;
            }
            break;
        }
      } catch (e) {
        // swallow
      }
    },

    onInputFocus() {
      this.active = true;
    },

    onTabFocus() {
      this.onInputFocus();

      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },

    onInputBlur() {
      if (this.mouseover === 'menu') return;

      this.active = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.select-input {
  margin-bottom: var(--select-input-margin-bottom, $gp);
  position: relative;
}

.dropdown {
  width: 100%;
}

.input {
  background-color: var(--theme-background);
  border: solid 1px var(--theme-input-border);
  border-radius: $border-radius;
  width: 100%;
  padding: $gp * .5 $gp * .5 $gp * .5 $gp * .75;
  height: 48px;
  display: flex;
  align-items: center;

  &.disabled {
    opacity: 0.5;
  }

  .value {
    font-size: 16px;
    flex: 1;
  }

  input {
    font-size: 16px;
    flex: 1;
    background-color: var(--theme-background);
    border: none;
    outline: 0;
    display: none;

    &:disabled {
      cursor: pointer;
    }
  }

  &.searchable {
    .value {
      display: block;
    }
  }

  .arrow {
    font-size: 24px;
    color: $color-gray-5;
  }

  &.active {
    border-color: $color-green-1;

    &.searchable {
      .value {
        display: none;
      }

      input {
        display: block;
      }
    }
  }

  label {
    color: $color-gray-5;
    background-color: var(--theme-background);
    font-size: 12px;
    position: absolute;
    transform-origin: top left;
    transform: translate(-2px, -23px);
    font-weight: bold;
    padding: 0 3px;
  }
}
</style>
